const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

const DEFAULT_INITIAL_ITEM_COUNT = 4;

const pendingTransitionHandlers = new WeakMap<HTMLElement, (event: TransitionEvent) => void>();

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function clearPendingTransition(panel: HTMLElement): void {
  const pending = pendingTransitionHandlers.get(panel);
  if (!pending) return;

  panel.removeEventListener('transitionend', pending);
  pendingTransitionHandlers.delete(panel);
}

function setPanelInteractivity(panel: HTMLElement, interactive: boolean): void {
  panel.setAttribute('aria-hidden', interactive ? 'false' : 'true');

  panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach(element => {
    if (interactive) {
      const previousTabindex = element.getAttribute('data-accordion-tabindex');

      if (previousTabindex === null) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', previousTabindex);
        element.removeAttribute('data-accordion-tabindex');
      }

      return;
    }

    if (!element.hasAttribute('data-accordion-tabindex')) {
      const currentTabindex = element.getAttribute('tabindex');

      if (currentTabindex !== null) {
        element.setAttribute('data-accordion-tabindex', currentTabindex);
      }
    }

    element.setAttribute('tabindex', '-1');
  });
}

function expand(trigger: HTMLButtonElement, panel: HTMLElement): void {
  clearPendingTransition(panel);
  trigger.setAttribute('aria-expanded', 'true');
  setPanelInteractivity(panel, true);
  const content = panel.querySelector<HTMLElement>('[data-accordion-content]');
  const targetHeight = content ? content.offsetHeight : panel.scrollHeight;

  if (prefersReducedMotion()) {
    panel.style.height = 'auto';
    return;
  }

  panel.style.height = `${targetHeight}px`;

  const handleExpandEnd = (event: TransitionEvent): void => {
    if (event.target !== panel || event.propertyName !== 'height') return;

    clearPendingTransition(panel);
    panel.style.height = 'auto';
  };

  pendingTransitionHandlers.set(panel, handleExpandEnd);
  panel.addEventListener('transitionend', handleExpandEnd);
}

function collapse(trigger: HTMLButtonElement, panel: HTMLElement): void {
  clearPendingTransition(panel);
  trigger.setAttribute('aria-expanded', 'false');
  setPanelInteractivity(panel, false);

  if (prefersReducedMotion()) {
    panel.style.height = '0px';
    return;
  }

  panel.style.height = `${panel.getBoundingClientRect().height}px`;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });
  });
}

function initSeeMore(root: HTMLElement, items: HTMLElement[]): void {
  const toggleWrap = root.querySelector<HTMLElement>('[data-accordion-toggle-wrap]');
  const toggleButton = root.querySelector<HTMLButtonElement>('[data-accordion-toggle]');
  const toggleLabel = root.querySelector<HTMLElement>('[data-accordion-toggle-label]');

  if (!toggleWrap || !toggleButton || !toggleLabel) return;

  const rawCount = Number(root.dataset.initialItemCount);
  const initialCount = rawCount > 0 ? rawCount : DEFAULT_INITIAL_ITEM_COUNT;

  if (items.length <= initialCount) return;

  toggleWrap.removeAttribute('hidden');

  const moreLabel = toggleButton.dataset.moreLabel ?? 'See more';
  const lessLabel = toggleButton.dataset.lessLabel ?? 'See less';
  let expanded = false;

  function update(): void {
    items.forEach((item, index) => {
      if (index < initialCount) return;

      if (expanded) {
        item.removeAttribute('hidden');
      } else {
        const trigger = item.querySelector<HTMLButtonElement>('[data-accordion-trigger]');
        const panel = item.querySelector<HTMLElement>('[data-accordion-panel]');

        if (trigger && panel && trigger.getAttribute('aria-expanded') === 'true') {
          collapse(trigger, panel);
        }

        item.setAttribute('hidden', '');
      }
    });

    toggleButton!.setAttribute('aria-expanded', String(expanded));
    toggleLabel!.textContent = expanded ? lessLabel : moreLabel;
  }

  toggleButton.addEventListener('click', () => {
    expanded = !expanded;
    update();
  });

  update();
}

export default function accordion(root: HTMLElement): void {
  const items = Array.from(root.querySelectorAll<HTMLElement>('[data-accordion-item]'));

  items.forEach(item => {
    const trigger = item.querySelector<HTMLButtonElement>('[data-accordion-trigger]');
    const panel = item.querySelector<HTMLElement>('[data-accordion-panel]');

    if (!trigger || !panel) return;

    if (trigger.getAttribute('aria-expanded') === 'true') {
      setPanelInteractivity(panel, true);
      panel.style.height = 'auto';
    } else {
      setPanelInteractivity(panel, false);
      panel.style.height = '0px';
    }

    trigger.addEventListener('click', () => {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        collapse(trigger, panel);
      } else {
        expand(trigger, panel);
      }
    });
  });

  initSeeMore(root, items);
}
